import React, {useEffect, useRef, useState} from 'react'
import Drawer from 'react-modern-drawer'
import {useAuth} from '../../../../modules/auth'
import {
  addDoc,
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
} from 'firebase/firestore'
import db from '../../../../../services/firebase'
import {useNavigate} from 'react-router-dom'
import {createBitbucketIntegration} from 'app/Integrations/Bitbucket'
import {showErrorToast, showInfoToast, showSuccessToast} from 'app/components/ShowToast'
import DrawerHeader from '../drawer-components/DrawerHeader'
import {DrawerContent} from '../drawer-components/DrawerContent'
import Overlay from 'app/components/Overlay'
import InfoCard from '../drawer-components/InfoCard'
import DrawerFooter from '../drawer-components/DrawerFooter'
import {AddDrawerIntegrationProps, BitbucketRepository, BitbucketWorkspace} from '../utils/types'

const BITBUCKET_CLIENT_ID = process.env.REACT_APP_BITBUCKET_CLIENT_ID || ''
const REDIRECT_URI = process.env.REACT_APP_BITBUCKET_REDIRECT_URI || ''

const BitbucketDrawer: React.FC<AddDrawerIntegrationProps> = ({id, isOpen, onClose}) => {
  const [isLoading, setIsLoading] = useState(false)
  const [openAccordion, setOpenAccordion] = useState(false)
  const [openAccordion2, setOpenAccordion2] = useState(false)
  const {currentUser} = useAuth()
  const [showAlert, setShowAlert] = useState(false)
  const drawerTopRef = useRef<HTMLDivElement>(null)
  const [integration, setIntegration] = useState<any[]>([])
  const [selectedWorkspace, setSelectedWorkspace] = useState('')
  const isConnected = integration.length > 0 && integration[0]?.status === 'Connected'
  const navigate = useNavigate()
  const [accessToken, setAccessToken] = useState<string | null>(
    localStorage.getItem('bitbucket_access_token')
  )
  const [formErrors, setFormErrors] = useState<Record<string, string>>({})
  const [isWorkspaceLocked, setIsWorkspaceLocked] = useState(false)
  const [workspaceSelecting, setWrokspaceSelecting] = useState(false)

  useEffect(() => {
    if (isOpen && id) {
      getIntegration();
    }
    if (isOpen && !selectedWorkspace) {
      setFormErrors((prev) => ({ ...prev, selectedWorkspace: 'Please select a workspace' }));
    } else if (isOpen && selectedWorkspace) {
      setFormErrors((prev) => ({ ...prev, selectedWorkspace: '' }));
    }
  }, [id, isOpen, selectedWorkspace]);

  const getIntegration = () => {
    try {
      const tenantId = currentUser?.tenantId || ''
      if (!id) return
      const integrationRef = doc(db, 'tenants', tenantId, 'integrations', id)
      const unsubscribe = onSnapshot(integrationRef, (doc) => {
        if (doc.exists()) {
          const data = doc.data()
          if (data.name === 'Bitbucket') {
            const integrationData = {
              ...data,
              id: doc.id,
              name: data.name,
              accessToken: data.accessToken,
              workspaces: data.workspaces || [],
              workspaceId: data.workspaceId || '',
            }
            setIntegration([integrationData])
            setAccessToken(data.accessToken)
            if (data.workspaceId !== undefined) {
              setSelectedWorkspace(data.workspaceId)
              setIsWorkspaceLocked(true)
            }
          }
        }
      })
      return unsubscribe
    } catch (error) {
      console.error('Error getting integration:', error)
    }
  }

  const handleWorkspaceSelect = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const workspaceSlug = e.target.value
    setSelectedWorkspace(workspaceSlug)

    if (!workspaceSlug) {
      setFormErrors((prev) => ({...prev, selectedWorkspace: 'Please select a workspace'}))
      return
    }
    setFormErrors((prev) => ({...prev, selectedWorkspace: ''}))
    try {
      setWrokspaceSelecting(true)
      const tenantId = currentUser?.tenantId || ''
      if (!id || !tenantId) return

      const integrationRef = doc(db, 'tenants', tenantId, 'integrations', id)
      const selectedWorkspaceData = integration[0].workspaces.find(
        (w: BitbucketWorkspace) => w.slug === workspaceSlug
      )

      await updateDoc(integrationRef, {
        workspaceId: workspaceSlug,
        selectedWorkspace: selectedWorkspaceData,
      })

      const bitbucketIntegration = createBitbucketIntegration({
        accessToken: accessToken || '',
        workspaceId: workspaceSlug,
      })

      const [reposResponse, membersResponse] = await Promise.all([
        bitbucketIntegration.getAllRepositories(),
        bitbucketIntegration.getWorkspaceMembers(),
      ])

      if (reposResponse.success && reposResponse.repositories) {
        const mappedRepositories = reposResponse.repositories.map((repo) => ({
          ...repo,
          pushed_at: repo.last_activity_at || repo.updated_on,
          html_url: repo.web_url,
          private: repo.visibility === 'private',
          stargazers_count: repo.star_count || 0,
          watchers_count: repo.watchers || 0,
          forks_count: repo.forks || 0,
          open_issues_count: repo.open_issues || 0,
        }))
        await handleSubmit(mappedRepositories)
      }
      if (membersResponse.success && membersResponse.members) {
        await saveWorkspaceMembers(membersResponse.members, workspaceSlug, tenantId)
      }

      // setIsWorkspaceLocked(true)
      showSuccessToast({message: 'Workspace selected and data loaded successfully'})
    } catch (error) {
      showErrorToast({message: 'Failed to process workspace selection'})
    } finally {
      setWrokspaceSelecting(false)
    }
  }

  const handleSubmit = async (repos: BitbucketRepository[]) => {
    setWrokspaceSelecting(true)
    let isAnyRepoAdded = false
    let addedReposCount = 0

    try {
      for (const repo of repos) {
        const newAsset = {
          is_repo: true,
          repo_name: repo.name || 'Unnamed Repository',
          repo_link: repo.html_url || '',
          platform: 'Bitbucket',
          visibility: repo.private ? 'Private' : 'Public',
          description: repo.description || '',
          created_at: repo.created_at
            ? new Date(repo.created_at).toISOString()
            : new Date().toISOString(),
          updated_at: repo.updated_on
            ? new Date(repo.updated_on).toISOString()
            : new Date().toISOString(),
          pushed_at: repo.pushed_at
            ? new Date(repo.pushed_at).toISOString()
            : new Date().toISOString(),
          size: repo.size || 0,
          stars: repo.stargazers_count || 0,
          watchers: repo.watchers_count || 0,
          language: repo.language || '',
          forks: repo.forks_count || 0,
          open_issues: repo.open_issues_count || 0,
          default_branch: repo.default_branch || 'main',
          owner: {
            login: repo.owner?.login || '',
            id: repo.owner?.uuid || '',
            url: repo.owner?.html_url || '',
            type: repo.owner?.type || '',
          },
          // workspace: {
          //   name: repo.workspace?.name || '',
          //   slug: repo.workspace?.slug || '',
          //   uuid: repo.workspace?.uuid || '',
          //   type: repo.workspace?.type || '',
          // },
          tenantId: currentUser?.tenantId || '',
          uid: currentUser?.uid || '',
          createdAt: serverTimestamp(),
          audit_scope: true,
        }

        if (currentUser && currentUser.tenantId) {
          const tenantRef = doc(db, 'tenants', currentUser.tenantId)
          const policyRef = collection(tenantRef, 'assets')

          const querySnapshot = await getDocs(
            query(policyRef, where('repo_link', '==', repo.html_url))
          )

          if (querySnapshot.empty) {
            await addDoc(policyRef, newAsset)
            addedReposCount++
            isAnyRepoAdded = true

            const currentDate = new Date()
            const todaysDate = `${
              currentDate.getMonth() + 1
            }/${currentDate.getDate()}/${currentDate.getFullYear()}`

            const activitiesRef = collection(tenantRef, 'activities')
            await addDoc(activitiesRef, {
              message: `${currentUser?.userName} added a new asset '${repo.name}'`,
              timestamp: serverTimestamp(),
              tenantId: currentUser?.tenantId,
              uid: currentUser?.uid,
              action: 'Asset added',
              target: 'Asset Management',
              email: currentUser?.email,
              username: currentUser?.userName,
              date: todaysDate,
            })
          }
        }
      }

      if (isAnyRepoAdded && currentUser && currentUser.tenantId) {
        const tenantRef = doc(db, 'tenants', currentUser.tenantId)
        const bitbucketRef = collection(tenantRef, 'integrations')
        const vendorsRef = collection(tenantRef, 'vendors')
        const integrationSnapshot = await getDocs(
          query(bitbucketRef, where('name', '==', 'Bitbucket'))
        )

        if (integrationSnapshot.empty) {
          const currentDate = new Date()
          const formattedDate = `${currentDate.getFullYear()}-${String(
            currentDate.getMonth() + 1
          ).padStart(2, '0')}-${String(currentDate.getDate()).padStart(2, '0')}`

          const newData = {
            accessToken: accessToken,
            name: 'Bitbucket',
            group: 'Developer Tools',
            img_path: '/integrations/Bitbucket.svg',
            status: 'Connected',
            connectedAt: formattedDate,
            tenantId: currentUser.tenantId,
            uid: currentUser.uid,
            workspaceId: selectedWorkspace,
          }

          await addDoc(bitbucketRef, newData)

          const vendorData = {
            authentication_type: '',
            category: 'Developer Tools',
            created_at: serverTimestamp(),
            due_deligence_status: 'Incomplete',
            impact_assessment: [],
            logo: [
              {
                name: 'bitbucket_logo.png',
                size: 3395,
                url: '/integrations/Bitbucket.svg',
              },
            ],
            owner: currentUser?.userName || '',
            policy_url: 'https://bitbucket.org/security',
            reliance: '',
            risk_level: '',
            tenantId: currentUser.tenantId,
            terms_url: 'https://bitbucket.org/terms',
            two_factor: true,
            uid: currentUser.uid,
            updated_at: serverTimestamp(),
            vendor_name: 'Bitbucket',
            website_url: 'https://bitbucket.org',
          }
          await addDoc(vendorsRef, vendorData)
        }
      }

      if (isAnyRepoAdded) {
        showSuccessToast({message: 'New repositories added successfully'})
      } else {
        showInfoToast({message: 'No new repositories were added'})
      }

      handleClose()
      navigate('/integrations')
      return addedReposCount
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      showErrorToast({message: `Failed to add repositories: ${errorMessage}`})
      return 0
    } finally {
      setWrokspaceSelecting(false)
    }
  }

  const saveWorkspaceMembers = async (members: any[], workspaceSlug: string, tenantId: string) => {
    try {
      const accessReviewRef = doc(db, 'tenants', tenantId, 'access_review', 'bitbucket')
      const membersMap: {[key: string]: any} = {}
      for (const member of members) {
        if (member.accountId) {
          membersMap[member.accountId] = {
            platform: 'Bitbucket',
            name: member.username || '',
            accountId: member.accountId || '',
            uuid: member.uuid || '',
            workspaceId: workspaceSlug || '',
            status: 'active',
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
            uid: currentUser?.uid || '',
            tenantId,
            type: member.memberType || '',
            profileLink: member.links?.html?.href || '', 
            accountRole: member.role || '', 
            mfaEnabled: member.mfaEnabled || '', 
            workspaceName: member.workspaceName || '',
            workspace: {
              slug: member.workspace?.slug || selectedWorkspace || '',
              name: member.workspace?.name || '',
              type: member.workspace?.type || 'workspace',
            },
          }
        }
      }

      const bitbucketDoc = await getDoc(accessReviewRef)

      if (bitbucketDoc.exists()) {
        await updateDoc(accessReviewRef, {
          members: {
            ...bitbucketDoc.data().members,
            ...membersMap,
          },
          updatedAt: serverTimestamp(),
        })
      } else {
        await setDoc(accessReviewRef, {
          platform: 'Bitbucket',
          members: membersMap,
          tenantId,
          uid: currentUser?.uid || '',
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp(),
          workspaceId: workspaceSlug || '',
        })
      }
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      console.error('Error saving workspace members:', errorMessage)
      showErrorToast({message: 'Failed to save workspace members'})
      throw error
    }
  }

  const WorkspaceSelector = () => {
    if (!integration[0]?.workspaces?.length) return null

    return (
      <div className='mb-4'>
        <label className='fw-semibold mb-2 fs-6'>Select Workspace</label>
        <select
          className='form-select'
          value={selectedWorkspace}
          onChange={handleWorkspaceSelect}
          disabled={isWorkspaceLocked}
        >
          <option value=''>Select Workspace</option>
          {integration[0].workspaces.map((workspace: BitbucketWorkspace) => (
            <option key={workspace.slug} value={workspace.slug}>
              {workspace.name}
            </option>
          ))}
        </select>
        {formErrors.selectedWorkspace && (
          <span className='text-danger'>{formErrors.selectedWorkspace}</span>
        )}
      </div>
    )
  }

  useEffect(() => {
    if (showAlert && drawerTopRef.current) {
      drawerTopRef.current.scrollIntoView({behavior: 'smooth'})
    }
  }, [showAlert])

  const handleConnect = async () => {
    try {
      setIsLoading(true)
      localStorage.setItem('pendingIntegration', 'Bitbucket')
      const encodedRedirectUri = encodeURIComponent(REDIRECT_URI)
      const authUrl = `https://bitbucket.org/site/oauth2/authorize?client_id=${BITBUCKET_CLIENT_ID}&redirect_uri=${encodedRedirectUri}&response_type=code&scope=repository+account+team+email`
      window.location.href = authUrl
      return
    } catch (error) {
      showErrorToast({message: 'Error connecting to Bitbucket'})
    } finally {
      setIsLoading(false)
    }
  }

  const handleClose = () => {
    setShowAlert(false)
    onClose()
  }

  const handleDisconnect = async () => {
    try {
      setIsLoading(true)
      const tenantId = currentUser?.tenantId || ''
      if (!id || !tenantId) return
      const tenantRef = doc(db, 'tenants', tenantId)

      const assetsRef = collection(tenantRef, 'assets')
      const bitbucketAssetsQuery = query(assetsRef, where('platform', '==', 'Bitbucket'))
      const bitbucketAssetsSnapshot = await getDocs(bitbucketAssetsQuery)

      const deleteAssetPromises = bitbucketAssetsSnapshot.docs.map(async (assetDoc) => {
        await deleteDoc(doc(db, 'tenants', tenantId, 'assets', assetDoc.id))
      })

      await Promise.all(deleteAssetPromises)

      const integrationRef = doc(db, 'tenants', tenantId, 'integrations', id)
      await deleteDoc(integrationRef)

      const vendorsRef = collection(tenantRef, 'vendors')
      const bitbucketVendorQuery = query(vendorsRef, where('vendor_name', '==', 'Bitbucket'))
      const bitbucketVendorSnapshot = await getDocs(bitbucketVendorQuery)

      const deleteVendorPromises = bitbucketVendorSnapshot.docs.map(async (vendorDoc) => {
        await deleteDoc(doc(db, 'tenants', tenantId, 'vendors', vendorDoc.id))
      })

      await Promise.all(deleteVendorPromises)

      localStorage.removeItem('bitbucket_access_token')

      const activitiesRef = collection(tenantRef, 'activities')
      const currentDate = new Date()
      const todaysDate = `${
        currentDate.getMonth() + 1
      }/${currentDate.getDate()}/${currentDate.getFullYear()}`
      await addDoc(activitiesRef, {
        message: `${currentUser?.userName} disconnected Bitbucket integration`,
        timestamp: serverTimestamp(),
        tenantId: currentUser?.tenantId,
        uid: currentUser?.uid,
        action: 'Integration disconnected',
        target: 'Integration Management',
        email: currentUser?.email,
        username: currentUser?.userName,
        date: todaysDate,
      })
      showSuccessToast({message: 'Bitbucket integration disconnected'})

      setIntegration([])
      setSelectedWorkspace('')
      setIsWorkspaceLocked(false)
      handleClose()
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Unknown error'
      console.error('Error disconnecting Bitbucket:', errorMessage)
      showErrorToast({message: 'Error disconnecting Bitbucket'})
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Drawer open={isOpen} onClose={handleClose} direction='right' className='' size='45%'>
      <DrawerHeader title='Bitbucket' onClose={onClose} />
      <div
        className='scroll-y overflow-auto p-6 mb-10'
        tabIndex={-1}
        style={{
          height: 'calc(100vh - 120px)',
          paddingBottom: '80px',
        }}
      >
        <div className='form'>
          <DrawerContent
            title='Bitbucket'
            description='Code Collaboration & Version Control Platform'
            logo='/integrations/bitbucket.svg'
            integration={integration}
            openAccordion={openAccordion}
            setOpenAccordion={setOpenAccordion}
            openAccordion2={openAccordion2}
            setOpenAccordion2={setOpenAccordion2}
            permissionInfo='SecureSlate will be granted permissions to read Bitbucket repositories and issues via OAuth 2.0, respecting user permissions.'
            accessTypeinfo='Requires an active Bitbucket integration with sufficient permissions to access repositories and issue data.'
          />

          <>
            <div className='mb-8 d-flex justify-content-between align-items-center'>
              <div className='fs-6 fw-bold text-bold'>
                {isConnected
                  ? 'Bitbucket Connected to SecureSlate'
                  : 'Connect Bitbucket to SecureSlate'}{' '}
                <Overlay
                  content='Upon successful connection, all projects within your designated Bitbucket
                        group will be automatically imported and presented within the Asset
                        Repository section of the platform for your review and management.'
                />
              </div>
              {workspaceSelecting && (
              <span className='badge px-3 rounded-pill ms-4' style={{border: '2px solid #E1E1E1'}}>
                <span className='fs-7'>Updating...</span>
              </span>
            )}
            </div>
            <div className='mb-4'>
              <WorkspaceSelector />
            </div>
            {isConnected &&
            <InfoCard
              type='info'
              title='How to connect Bitbucket with SecureSlate?'
              description='Click on the ‘Connect’ button below. This will initiate an OAuth connection to Bitbucket. Once connected, the system will automatically fetch and set up all required configurations.'
            />
            }
          </>
        </div>
      </div>
      <DrawerFooter
        isConnected={isConnected}
        isLoading={isLoading}
        onConnect={handleConnect}
        onDisconnect={handleDisconnect}
        customActions={!isConnected}
      />
    </Drawer>
  )
}

export default BitbucketDrawer
